import type { ConfigData } from 'serviceWorker/api/types';
import { DEFAULT_API_SYNC_DEBOUNCE_MS } from 'serviceWorker/const/syncTiming';
import { debounceNext } from 'serviceWorker/helpers/debounce';
import type { PullingContract } from 'serviceWorker/helpers/broadcastPullError';
import type { Services } from 'shared/domain/messages/message';
import {
  getFinishedServiceStateWithoutSyncKey,
  getFinishedServiceStateWithSyncKey,
} from './getFinishedServiceState';
import { makePullEntity } from './makePullEntity';
import { makePullHandler } from './makePullHandler';
import { makePullPagedEntity } from './makePullPagedEntity';
import { makeSynchronizeEntity } from './makeSynchronizeEntity';
import { createPendingPublish } from './pendingPublish';
import { markPullStep, PullStep } from './pullProgress';
import { runPullInBackground } from './runPullInBackground';
import type { Pull } from './types';

type SynchronizeProps<T> = Parameters<typeof makeSynchronizeEntity<T>>[0];

type FetchAll = Parameters<typeof makePullEntity>[0]['fetch'];

type FetchPage = Parameters<typeof makePullPagedEntity>[0]['fetchPaged'];

/** Downloads the whole collection at once, or page by page (large entities). */
type FetchCollection = { fetch: FetchAll } | { fetchPaged: FetchPage };

type PullHandlerProps = Parameters<typeof makePullHandler>[0];

interface SyncedEntityBase<TRepository, TService> {
  entityName: Services;
  contract: PullingContract;
  repository: TRepository;
  service: TService;
  /** The collection event (`{ items, hasAll }`), published after each sync the page asked for. */
  publishCollection: () => unknown;
  /** Config the pull needs (default: locale, frontend version, API and project). */
  requiredConfig?: (keyof ConfigData)[];
  /**
   * Full downloads replace the stored items in one go (`addBatch` puts the new
   * list and removes the missing ones), so they stay readable while the list
   * downloads again instead of being cleared first, and a failed download
   * keeps them too. Default: cleared.
   */
  keepStoredWhileDownloading?: boolean;
}

type PulledRepository<T> = PullHandlerProps['entityRepository'] & {
  addBatch: (items: T[]) => Promise<unknown>;
};

/** Downloads the collection once, then only the changes since its sync key. */
type IncrementalEntity<T> = SyncedEntityBase<
  PulledRepository<T> & SynchronizeProps<T>['entityRepository'],
  SynchronizeProps<T>['entityService']
> &
  FetchCollection & {
    fetchUpdated: SynchronizeProps<T>['fetchUpdatedEntities'];
    /** Skips every sync and download when false (e.g. no access for the user's role). */
    canSynchronize?: () => Promise<boolean>;
  };

/** Has no sync key: every sync downloads the whole collection again. */
type FullyDownloadedEntity<T> = SyncedEntityBase<PulledRepository<T>, PullHandlerProps['entityService']> & {
  fetch: FetchAll;
};

export type SyncedEntityConfig<T> = IncrementalEntity<T> | FullyDownloadedEntity<T>;

export interface SyncedEntity {
  /** Syncs from the API now and resolves once done: whether the result was stored. */
  syncAndWait(): Promise<boolean>;
  /**
   * Syncs from the API now and resolves once done. Never rejects: a failure is
   * logged with the step the sync got to.
   */
  syncInBackground(): Promise<void>;
  /** Requests a sync from the API and returns at once, at most once per {@link DEFAULT_API_SYNC_DEBOUNCE_MS}. */
  sync(): void;
  /**
   * Downloads the collection only when it is not downloaded: a downloaded one
   * is neither downloaded again nor published (a running download publishes
   * when it ends). An entity the user has no access to (`canSynchronize`) is
   * not downloaded either. Never rejects: a failure is logged with the step the
   * pull got to.
   */
  downloadWhenMissing(): Promise<void>;
}

function isIncremental<T>(config: SyncedEntityConfig<T>): config is IncrementalEntity<T> {
  return 'fetchUpdated' in config;
}

/**
 * The download and sync of an entity kept in IndexedDB: the pull (guarded,
 * aborted on logout / project change), the incremental sync and the collection
 * event the page waits for, published once per finished download or sync.
 */
export function createSyncedEntity<T>(config: SyncedEntityConfig<T>): SyncedEntity {
  const { entityName, contract, repository, service, publishCollection, requiredConfig, keepStoredWhileDownloading } =
    config;
  const incremental = isIncremental(config);
  const pendingCollection = createPendingPublish(publishCollection);
  const finishedServiceStateFactory = incremental
    ? getFinishedServiceStateWithSyncKey
    : getFinishedServiceStateWithoutSyncKey;

  const pull: Pull =
    'fetchPaged' in config
      ? makePullPagedEntity({
          fetchPaged: config.fetchPaged,
          channelContract: contract,
          entityName,
          entityRepository: repository,
          entityService: service,
          finishedServiceStateFactory,
          pendingCollection,
        })
      : makePullEntity({
          fetch: config.fetch,
          channelContract: contract,
          entityName,
          entityRepository: repository,
          entityService: service,
          finishedServiceStateFactory,
          pendingCollection,
          withoutSyncKey: !incremental,
          keepStoredOnFailure: keepStoredWhileDownloading,
        });

  const pullHandler = makePullHandler({
    entityRepository: repository,
    entityService: service,
    resync: pull,
    pendingCollection,
    customRequirements: requiredConfig,
    withoutSyncKey: !incremental,
    clearBeforePull: !keepStoredWhileDownloading,
    channelContract: contract,
    entityName,
  });

  const synchronize = incremental
    ? makeSynchronizeEntity<T>({
        entityRepository: config.repository,
        entityService: config.service,
        pullEntityHandler: pullHandler,
        fetchUpdatedEntities: config.fetchUpdated,
        finishedServiceStateFactory,
        entityName,
        channelContract: contract,
        pendingCollection,
      })
    : async (): Promise<boolean> => {
        pendingCollection.request();
        await pullHandler();
        // A skipped pull answers with the stored collection; a running one publishes when it ends.
        if (!pullHandler.isRunning()) {
          pendingCollection.flush();
        }
        return Boolean((await service.get())?.isDownloaded);
      };

  const canSynchronize = incremental ? config.canSynchronize : undefined;
  /**
   * Every sync and download goes through it: one rule for the user's access
   * to the entity. Without a rule nothing is awaited, the sync starts at once.
   */
  const whenAllowed = <TResult>(run: () => Promise<TResult>, skipped: TResult): (() => Promise<TResult>) =>
    canSynchronize
      ? async () => {
          if (await canSynchronize()) {
            return run();
          }
          markPullStep(contract, PullStep.notAllowed);
          return skipped;
        }
      : run;

  const syncAndWait = whenAllowed(synchronize, false);
  const syncInBackground = (): Promise<void> => runPullInBackground({ entityName, contract }, syncAndWait);
  // The pull asks for the collection only when it starts: requesting and
  // cancelling here would drop the publish a concurrent sync asked for.
  // The access rule runs inside the background run too: its failure is logged, not an unhandled rejection.
  const downloadWhenMissing = (): Promise<void> =>
    runPullInBackground({ entityName, contract }, whenAllowed(() => pullHandler({ onlyWhenMissing: true }), undefined));

  return {
    syncAndWait,
    syncInBackground,
    downloadWhenMissing,
    sync: debounceNext(syncInBackground, DEFAULT_API_SYNC_DEBOUNCE_MS),
  };
}
